import { format, isSameDay } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import type { GuestSpot, Artist } from '@/types/models'
import { Calendar, CheckCircle2, Edit2, MapPin, Send, Trash2 } from 'lucide-react'
import { CountryFlag } from '@/lib/countryFlags'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useImageLoadAndVisible } from '@/hooks/useImageLoadAndVisible'
import { useUserData } from '@/hooks/useUserData'
import { getGuestSpotPortfolioImageUrls, normalizeInstagramHandle } from '@/lib/localGuestSpots'

interface GuestSpotCardProps {
  guestSpot: GuestSpot
  artist?: Artist
  isAdmin?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

const avatarSizeClasses = 'h-16 w-16 md:h-20 md:w-20'

function normalizeInstagramStudioHandle(rawHandle: string | undefined): string | undefined {
  if (!rawHandle) return undefined

  const normalizedHandle = rawHandle
    .trim()
    .replace(/^(?:guesting\s+(?:at|in)\s+)+/i, '')
    .replace(/^@+/, '')
    .trim()

  return normalizedHandle || undefined
}

function formatGuestSpotDateRange(startDate: Date, endDate: Date): string {
  if (isSameDay(startDate, endDate)) {
    return format(startDate, 'MMM d, yyyy')
  }

  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
  }

  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
}

function GuestSpotPortfolioImage({ imageUrl }: { imageUrl: string }) {
  return (
    <div className="relative aspect-square min-h-0 w-full overflow-hidden rounded-xl bg-muted">
      <img
        src={imageUrl}
        alt="Guest artist tattoo preview"
        className="h-full w-full object-cover"
        loading="lazy"
      />
    </div>
  )
}

function GuestSpotAvatar({
  avatarUrl,
  displayName,
  fallbackInitial,
  onImageError,
}: {
  avatarUrl?: string
  displayName: string
  fallbackInitial: string
  onImageError: () => void
}) {
  const [imageReady, setImageReady] = useState(false)

  const { imgRef, handleImageLoad } = useImageLoadAndVisible({
    onImageLoadAndVisible: () => {
      setImageReady(true)
    },
    preloaded: false,
  })

  const hasAvatar = !!avatarUrl

  return (
    <div className="relative flex-shrink-0">
      <div
        className={`relative ${avatarSizeClasses} flex items-center justify-center rounded-2xl border-4 border-border shadow-md transition-opacity duration-500 ${
          imageReady && hasAvatar ? 'pointer-events-none opacity-0' : 'opacity-100'
        } ${imageReady && hasAvatar ? 'bg-transparent' : 'animate-pulse bg-muted'}`}
      >
        {!hasAvatar ? (
          <span className="text-lg font-medium">{fallbackInitial}</span>
        ) : null}
      </div>

      {hasAvatar ? (
        <img
          ref={imgRef}
          src={avatarUrl}
          alt={displayName}
          className={`absolute inset-0 ${avatarSizeClasses} rounded-2xl border-4 border-border object-cover shadow-md transition-opacity duration-500 ${
            imageReady ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleImageLoad}
          onError={(event) => {
            event.currentTarget.style.opacity = '0'
            onImageError()
          }}
        />
      ) : null}
    </div>
  )
}

export function GuestSpotCard({ guestSpot, artist, isAdmin = false, onEdit, onDelete }: GuestSpotCardProps) {
  const navigate = useNavigate()
  const [imageError, setImageError] = useState(false)
  const { trackEvent } = useAnalytics()
  const { isOwnArtistPage, isAdminModeActive } = useUserData()

  const isAutomatedSpot = !!guestSpot.username
  const isManualSpot = !!guestSpot.artistId

  const instagramArtistHandle = normalizeInstagramHandle(guestSpot.username)
  const instagramArtistUrl = instagramArtistHandle
    ? `https://instagram.com/${instagramArtistHandle}`
    : null

  const hideMessageButton = !artist || isOwnArtistPage(artist.id) || isAdminModeActive

  const handleCardClick = () => {
    if (artist) {
      navigate(`/artist/${artist.id}`)
      return
    }

    if (isManualSpot && guestSpot.artistId) {
      navigate(`/artist/${guestSpot.artistId}`)
      return
    }

    if (isAutomatedSpot && instagramArtistUrl && guestSpot.username) {
      trackEvent('guest_instagram_clicked', {
        instagram_handle: guestSpot.username,
        city: guestSpot.targetCity,
      })
      window.open(instagramArtistUrl, '_blank')
    }
  }

  const handleMessageArtistClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (!artist) return

    trackEvent('artist_message_clicked', {
      artist_id: artist.id,
      source: 'guest_artists',
    })
    navigate(`/chats/new?artistId=${artist.id}&source=guest_artists`)
  }

  const displayName =
    artist?.displayName
      ? `@${normalizeInstagramHandle(artist.displayName) ?? artist.displayName}`
      : isAutomatedSpot && guestSpot.username
        ? `@${normalizeInstagramHandle(guestSpot.username) ?? guestSpot.username}`
        : isManualSpot
          ? 'Unknown Artist'
          : 'Unknown Artist'

  const avatarUrl =
    artist
      ? artist.compressedAvatarUrl || artist.avatarUrl || guestSpot.profileImageUrl
      : isAutomatedSpot
        ? guestSpot.profileImageUrl
        : undefined

  const fallbackInitial =
    artist?.displayName
      ? artist.displayName.replace(/^@+/, '').charAt(0).toUpperCase()
      : isAutomatedSpot && guestSpot.username
        ? guestSpot.username.replace(/^@+/, '').charAt(0).toUpperCase()
        : '?'

  const homeCity = artist?.location.city || guestSpot.homeCity
  const homeCountry = artist?.location.country || guestSpot.homeCountry
  const homeLocationLabel =
    homeCity && homeCountry
      ? `${homeCity}, ${homeCountry}`
      : homeCity || homeCountry || undefined

  const trimmedManualStudioName = guestSpot.studioName?.trim()
  const instagramStudioHandle =
    normalizeInstagramStudioHandle(guestSpot.targetStudioUsername) ??
    normalizeInstagramStudioHandle(guestSpot.sourceStudioUsername) ??
    normalizeInstagramStudioHandle(trimmedManualStudioName)

  const portfolioImageUrls = getGuestSpotPortfolioImageUrls(guestSpot)

  return (
    <div
      onClick={handleCardClick}
      className="relative w-full min-w-0 cursor-pointer overflow-hidden rounded-2xl border border-border/70 bg-card p-2 shadow-sm transition-colors hover:bg-card/95"
    >
      {isAdmin && (
        <div className="absolute right-2 top-2 z-20 flex gap-2">
          <button
            onClick={(event) => {
              event.stopPropagation()
              onEdit?.()
            }}
            className="rounded-md border border-border bg-background/95 p-2 shadow-sm backdrop-blur-sm transition-colors hover:bg-accent"
            title="Edit guest spot"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={(event) => {
              event.stopPropagation()
              onDelete?.()
            }}
            className="rounded-md border border-border bg-background/95 p-2 shadow-sm backdrop-blur-sm transition-colors hover:bg-destructive/10 hover:text-destructive"
            title="Archive guest spot"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className={`relative z-10 flex items-center gap-3 ${isAdmin ? 'pr-24' : ''}`}>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="relative">
            <GuestSpotAvatar
              avatarUrl={avatarUrl && !imageError ? avatarUrl : undefined}
              displayName={displayName}
              fallbackInitial={fallbackInitial}
              onImageError={() => setImageError(true)}
            />
            {homeCountry ? (
              <div className="absolute -bottom-1 -right-1 z-20">
                <CountryFlag country={homeCountry} size="lg" />
              </div>
            ) : null}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-lg font-medium leading-tight">{displayName}</h3>
              {guestSpot.isVerified ? (
                <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-blue-500" />
              ) : null}
            </div>
            {homeLocationLabel ? (
              <p className="truncate text-sm text-muted-foreground">{homeLocationLabel}</p>
            ) : null}
          </div>
        </div>

        {!hideMessageButton ? (
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={handleMessageArtistClick}
              className="m-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
              aria-label="Message artist"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        ) : null}
      </div>

      <div className="relative z-10 mt-2 space-y-2 px-1 text-sm text-muted-foreground">
        {instagramStudioHandle ? (
          <div className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <a
              href={`https://instagram.com/${instagramStudioHandle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="truncate underline-offset-2 hover:text-foreground hover:underline"
            >
              Guesting in @{instagramStudioHandle}
            </a>
          </div>
        ) : trimmedManualStudioName ? (
          <div className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Guesting in {trimmedManualStudioName}</span>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 flex-shrink-0" />
          <span>{formatGuestSpotDateRange(guestSpot.startDate, guestSpot.endDate)}</span>
        </div>
      </div>

      {portfolioImageUrls ? (
        <div className="relative z-10 mt-2 grid w-full min-w-0 grid-cols-3 gap-1.5">
          {portfolioImageUrls.map((imageUrl, index) => (
            <GuestSpotPortfolioImage key={`${guestSpot.id}-${index}`} imageUrl={imageUrl} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
